import { useState, useCallback } from 'react';
import { useCreateAttributeBindingMutation, useSetTaxonomyAttributeValueMutation } from '../../api/cropsApi';
import { toastError, toastSuccess } from '../../utils/toast';
import type { TaxonomyScope } from '../../models/Crop';
import i18n from '../../i18n';

interface UseAttributeBindingProps {
  saveId: string;
  onSuccess?: () => Promise<void>;
}

export const useAttributeBinding = ({ saveId, onSuccess }: UseAttributeBindingProps) => {
  const [attributeToBind, setAttributeToBind] = useState<string>();
  const [bindingValue, setBindingValue] = useState<{ choiceId?: string | null; customValue?: string | null }>({});

  const [createAttributeBinding, { isLoading: creatingBinding }] = useCreateAttributeBindingMutation();
  const [setTaxonomyAttributeValue] = useSetTaxonomyAttributeValueMutation();

  const handleAddAttributeToScope = useCallback(
    async (scope: TaxonomyScope, scopeId: number | string, selectedAttribute: any) => {
      if (!scope || !attributeToBind) {
        toastError(i18n.t('crops:attributes.binding.selectAttribute'));
        return;
      }
      if (!saveId) {
        toastError(i18n.t('crops:details.errors.noSave'));
        return;
      }

      try {
        const numericAttributeId = Number(attributeToBind);
        const bindingPayload = {
          attributeId: Number.isNaN(numericAttributeId) ? attributeToBind : numericAttributeId,
          scope,
          scopeId,
          saveId,
        };
        await createAttributeBinding(bindingPayload).unwrap();

        // Если указано значение по умолчанию, устанавливаем его
        const hasOptions = selectedAttribute?.options && selectedAttribute.options.length > 0;
        const hasValue = hasOptions
          ? bindingValue.choiceId != null
          : bindingValue.customValue != null && bindingValue.customValue.trim() !== '';

        if (hasValue) {
          const valuePayload: any = {
            scope,
            scopeId,
            attributeId: numericAttributeId,
            saveId,
          };

          if (hasOptions) {
            const choiceId = bindingValue.choiceId;
            valuePayload.choiceId = choiceId ? (isNaN(Number(choiceId)) ? choiceId : Number(choiceId)) : null;
            valuePayload.customValue = null;
          } else {
            valuePayload.choiceId = null;
            valuePayload.customValue = bindingValue.customValue?.trim() || null;
          }

          await setTaxonomyAttributeValue(valuePayload).unwrap();
        }

        toastSuccess(i18n.t('crops:attributes.binding.bound'));
        setAttributeToBind(undefined);
        setBindingValue({});

        if (onSuccess) {
          await onSuccess();
        }
      } catch (error) {
        toastError(error);
      }
    },
    [attributeToBind, bindingValue, saveId, createAttributeBinding, setTaxonomyAttributeValue, onSuccess],
  );

  const resetBinding = useCallback(() => {
    setAttributeToBind(undefined);
    setBindingValue({});
  }, []);

  return {
    attributeToBind,
    setAttributeToBind,
    bindingValue,
    setBindingValue,
    creatingBinding,
    handleAddAttributeToScope,
    resetBinding,
  };
};
